import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PosizioneFrigoSelect } from "./PosizioneFrigoSelect";

/**
 * Dialog HACCP per congelare un lotto.
 * Mostra: data congelamento, durata in mesi (oppure data scadenza custom), note.
 */
const CongelaLottoDialog = ({
  lotto,
  open,
  onOpenChange,
  form,
  setForm,
  saving,
  onConferma,
  getNomeIngrediente,
  frigoriferi = [], posizioni = [],
}) => {
  const metodo = form.metodo || "congelatore";
  const isAbbattitore = metodo === "abbattitore";
  const qTot = lotto?.quantita_preparata;
  const qTotNum = qTot != null && qTot !== "" && !isNaN(parseFloat(qTot)) ? parseFloat(qTot) : null;
  const um = lotto?.unita_misura || "kg";
  const qInserita = parseFloat(form.quantita_parziale);
  const isParziale = qTotNum != null && !isNaN(qInserita) && qInserita > 0 && qInserita < qTotNum;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="text-xl">{isAbbattitore ? "⚡" : "❄️"}</span>
            {isAbbattitore ? "Abbatti lotto" : "Congela lotto"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-muted-foreground">
            <strong>{lotto && getNomeIngrediente(lotto.ingrediente_id, lotto.prodotto_id, lotto)}</strong> — Lotto {lotto?.numero_lotto || "-"}
          </p>

          {/* Selettore metodo */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Metodo</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, metodo: "congelatore" }))}
                className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                  metodo === "congelatore"
                    ? "border-sky-500 bg-sky-50 shadow-sm"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid="metodo-congelatore"
              >
                <div className="text-sm font-bold text-sky-700 flex items-center gap-1">❄️ Congelatore</div>
                <div className="text-[10px] text-slate-500 mt-0.5">-18°C, conservazione lunga</div>
              </button>
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, metodo: "abbattitore" }))}
                className={`p-2.5 rounded-xl border-2 text-left transition-all ${
                  metodo === "abbattitore"
                    ? "border-indigo-500 bg-indigo-50 shadow-sm"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid="metodo-abbattitore"
              >
                <div className="text-sm font-bold text-indigo-700 flex items-center gap-1">⚡ Abbattitore</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Raffreddamento rapido</div>
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">{isAbbattitore ? "Data abbattimento" : "Data congelamento"}</Label>
            <Input
              type="date"
              value={form.data}
              onChange={(e) => setForm((p) => ({ ...p, data: e.target.value }))}
              data-testid="congela-data"
            />
          </div>
          <PosizioneFrigoSelect value={isParziale ? (form.posizione ?? "") : (form.posizione ?? lotto?.posizione ?? "")}
            onChange={v => setForm(p => ({ ...p, posizione: v }))}
            frigoriferi={frigoriferi} posizioniLegacy={posizioni} testId="congela-posizione" />
          {isParziale && <p className="text-xs text-muted-foreground" data-testid="congela-posizione-figlio-avviso">Nello split la posizione riguarda solo il lotto figlio.</p>}

          {/* Quantità da congelare (obbligatoria) */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              Quantità da {isAbbattitore ? "abbattere" : "congelare"} <span className="text-red-500">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.001"
                max={qTotNum ?? undefined}
                value={form.quantita_parziale}
                onChange={(e) => setForm((p) => ({ ...p, quantita_parziale: e.target.value }))}
                placeholder={qTotNum != null ? `${qTotNum}` : "es. 2.5"}
                className="w-40"
                data-testid="congela-quantita"
              />
              <span className="text-sm text-muted-foreground">
                {qTotNum != null ? <>su {qTotNum} {um}</> : um}
              </span>
            </div>
            {qTotNum == null && (
              <p className="text-[11px] text-amber-600">
                Questo lotto non ha una quantità numerica: inserisci la quantità da congelare.
              </p>
            )}
            {isParziale && (
              <p className="text-[11px] text-muted-foreground">
                Congelamento parziale: verrà creato un nuovo lotto {isAbbattitore ? "abbattuto" : "congelato"} di <strong>{qInserita} {um}</strong> e l'originale resterà {lotto?.metodo_conservazione === "temp_ambiente" ? "a temp. ambiente" : "in frigo"} con <strong>{Math.round((qTotNum - qInserita) * 1000) / 1000} {um}</strong>.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Durata in congelatore</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                max="12"
                value={form.mesi}
                onChange={(e) => setForm((p) => ({ ...p, mesi: e.target.value, data_scadenza_custom: "" }))}
                className="w-24"
                data-testid="congela-mesi"
              />
              <span className="text-sm text-muted-foreground">mesi</span>
              <span className="text-xs text-muted-foreground ml-auto">oppure data specifica ↓</span>
            </div>
            <Input
              type="date"
              value={form.data_scadenza_custom}
              onChange={(e) => setForm((p) => ({ ...p, data_scadenza_custom: e.target.value }))}
              className="text-sm"
              placeholder="Data scadenza personalizzata"
              data-testid="congela-data-custom"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Note (opzionale)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              placeholder={isAbbattitore ? "es. abbattuto e porzionato" : "es. carne suina già porzionata"}
              data-testid="congela-note"
            />
          </div>
          <div className={`rounded-xl p-3 text-xs ${isAbbattitore ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400" : "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400"}`}>
            ℹ️ La nuova etichetta verrà stampata automaticamente con il QR code aggiornato.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            onClick={onConferma}
            disabled={saving || !(parseFloat(form.quantita_parziale) > 0)}
            className={`text-white ${isAbbattitore ? "bg-indigo-600 hover:bg-indigo-700" : "bg-sky-600 hover:bg-sky-700"}`}
            data-testid="congela-conferma"
          >
            {saving ? "..." : (isAbbattitore ? "⚡ Abbatti" : "❄️ Congela")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CongelaLottoDialog;
